import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import type { IAuthService } from '@/services/auth/auth.service.interface'
import type { User, LoginOptions, LogoutOptions, AuthResult } from '@/types/auth.types'
import {
  clearRememberMePreference,
  getRememberMePreference,
  shouldExpireSession,
  clearLastOrgId,
} from '@/utils/secure-storage'
import { extractOrgId } from '@/utils/token.utils'
import { gateRefusalCode, ORGANISATION_UNAVAILABLE_CODE } from '@/utils/organisation-access'
import { isInactivityExpired, updateLastActivity, clearLastActivity } from '@/utils/session.utils'
import { INACTIVITY_TIMEOUT_MS } from '@/utils/session.config'
import {
  clearAccountBlock,
  isInvitePath,
  recordAllOrganisationsDisabled,
  recordNoOrganisation,
  seatRefused,
} from '@/lib/account-access'
import { reportError } from '@/lib/error-reporting'
import { clear as clearPaymentIssueBannerDismissal } from '@/utils/payment-issue-banner-dismissal'
import { useCurrentOrganisationStore } from '@/stores/current-organisation.store'

export const useAuthStore = defineStore('auth', () => {
  // State
  const isAuthenticated = ref(false)
  const isLoading = ref(false)
  const user = ref<User | null>(null)
  const error = ref<Error | null>(null)
  /**
   * The signed-in user's organisation may not operate — suspended, closed or archived — so the
   * BFF's access gate refuses every call they make (#1809). Recomputed on every roles load, so a
   * reload after Site Compass reactivates the organisation clears it.
   */
  const organisationUnavailable = ref(false)
  /** The BFF refused an own-API request because the member holds no usable seat (#1848). */
  const seatRequired = ref(false)
  // Any own-API 403 SEAT_REQUIRED, seen by the account-access fetch watch, raises the flag.
  watch(seatRefused, refused => refused && (seatRequired.value = true), { immediate: true })

  // Auth service instance (injected for testability)
  let authService: IAuthService | null = null

  /**
   * Where the sign-in callback being handled right now will send the user (its `appState`
   * `returnTo`), or `null` outside one. Read by {@link blockIfNoOrganisation}: an invitee comes
   * back from Auth0 to `/invite/:token` with no organisation yet (#2791).
   */
  let callbackReturnTo: string | null = null

  // Set auth service (for dependency injection)
  function setAuthService(service: IAuthService) {
    authService = service
  }

  // Getters
  const currentUser = computed(() => user.value)

  function hasPermission(permission: string): boolean {
    return user.value?.permissions?.includes(permission) ?? false
  }

  function hasRole(role: string): boolean {
    const roles = user.value?.roles ?? []
    // #561: the 'admin' app-role was renamed to 'organisation-admin'. Treat the two as
    // equivalent so role checks work across the data migration (either value may be
    // stored) and regardless of which literal a caller passes. Drop after the legacy
    // 'admin' rows are fully migrated.
    if (role === 'admin' || role === 'organisation-admin') {
      return roles.includes('admin') || roles.includes('organisation-admin')
    }
    return roles.includes(role)
  }

  // Actions
  async function initialize(): Promise<AuthResult<void>> {
    if (!authService) {
      return {
        ok: false,
        error: new Error('Auth service not initialized'),
      }
    }

    try {
      isLoading.value = true
      error.value = null

      const result = await authService.initialize()
      if (!result.ok) {
        error.value = result.error
        return result
      }

      // Check if user is already authenticated
      await checkAuth()

      // After checking auth, apply the correct session expiry logic.
      if (isAuthenticated.value) {
        const rememberMe = await getRememberMePreference()

        if (rememberMe) {
          // Remember Me is ON: honour the 7-day hard limit but skip the
          // inactivity check — the user explicitly asked to stay logged in.
          // Also reset last-activity so the inactivity timer starts fresh
          // from this browser open.
          const hardExpired = await shouldExpireSession()
          if (hardExpired) {
            if (import.meta.env.DEV) {
              // eslint-disable-next-line no-console
              console.log('[AuthStore] Remember Me session exceeded 7-day limit. Logging out...')
            }
            await logout({ returnTo: window.location.origin })
            return {
              ok: false,
              error: new Error('Your saved session has expired. Please log in again.'),
            }
          }
          // Reset inactivity timer so the 6-hour window starts from now
          updateLastActivity()
        } else {
          // Remember Me is OFF: enforce the inactivity window (6 hours).
          if (isInactivityExpired(INACTIVITY_TIMEOUT_MS)) {
            if (import.meta.env.DEV) {
              // eslint-disable-next-line no-console
              console.log('[AuthStore] Session expired due to inactivity. Logging out...')
            }
            await logout({ returnTo: window.location.origin })
            return {
              ok: false,
              error: new Error('Session expired due to inactivity. Please log in again.'),
            }
          }
        }
      }

      return { ok: true, value: undefined }
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Initialization failed')
      error.value = errorObj
      return { ok: false, error: errorObj }
    } finally {
      isLoading.value = false
    }
  }

  async function login(options?: LoginOptions): Promise<AuthResult<void>> {
    if (!authService) {
      return {
        ok: false,
        error: new Error('Auth service not initialized'),
      }
    }

    try {
      isLoading.value = true
      error.value = null

      const result = await authService.login(options)
      if (!result.ok) {
        error.value = result.error
        return result
      }

      return { ok: true, value: undefined }
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Login failed')
      error.value = errorObj
      return { ok: false, error: errorObj }
    } finally {
      isLoading.value = false
    }
  }

  async function logout(options?: LogoutOptions): Promise<AuthResult<void>> {
    if (!authService) {
      return {
        ok: false,
        error: new Error('Auth service not initialized'),
      }
    }

    try {
      isLoading.value = true
      error.value = null

      // The signed-out user's current organisation is theirs, not the browser's (#2200): kept, the
      // NEXT person to sign in here would inherit it, and Settings → My Organisation would link to
      // an organisation they are not in. Cleared BEFORE the hand-off, because the Auth0 logout
      // navigates away and nothing after it is guaranteed to run.
      useCurrentOrganisationStore().clear()
      // Same reason: the failed-payment notice is shown again at the next sign-in (#1840).
      clearPaymentIssueBannerDismissal()

      const result = await authService.logout(options)

      // Clear state regardless of logout result
      isAuthenticated.value = false
      user.value = null

      // Clear remember me preference and last activity on logout
      clearRememberMePreference()
      clearLastActivity()

      if (!result.ok) {
        error.value = result.error
        return result
      }

      return { ok: true, value: undefined }
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Logout failed')
      error.value = errorObj
      return { ok: false, error: errorObj }
    } finally {
      isLoading.value = false
    }
  }

  async function handleCallback(): Promise<AuthResult<{ appState?: unknown }>> {
    if (!authService) {
      return {
        ok: false,
        error: new Error('Auth service not initialized'),
      }
    }

    try {
      isLoading.value = true
      error.value = null

      const result = await authService.handleRedirectCallback()
      if (!result.ok) {
        error.value = result.error
        return result
      }

      // A completed sign-in supersedes refusals of the token it replaced; if the new token is
      // refused, its /me records the block again.
      clearAccountBlock()

      // Update auth state after successful callback. The destination is known to the
      // no-organisation stop for the span of this check only (#2791).
      const returnTo = (result.value.appState as { returnTo?: unknown } | undefined)?.returnTo
      callbackReturnTo = typeof returnTo === 'string' ? returnTo : null
      try {
        await checkAuth()
      } finally {
        callbackReturnTo = null
      }

      // A new sign-in, by anyone, shows the failed-payment notice again (#1840).
      clearPaymentIssueBannerDismissal()

      // Purges the value older builds saved; nothing reads it since #2909.
      try {
        clearLastOrgId()
      } catch {
        // best-effort
      }

      return result
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Callback handling failed')
      error.value = errorObj
      return { ok: false, error: errorObj }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Load user roles from database and merge with Auth0 roles
   * @param _userId - User ID to load roles for (reserved for future use)
   */
  async function loadDatabaseRoles(_userId: string): Promise<boolean> {
    try {
      // Get access token for API authentication
      const tokenResult = await authService?.getAccessToken()
      if (!tokenResult?.ok || !tokenResult.value) {
        if (import.meta.env.DEV) {
          console.warn('[AuthStore] Failed to get access token for role loading')
        }
        // Fail closed (#561): the DB is the single source of truth for authorization, so on
        // ANY failure clear roles to [] — never leave stale Auth0-claim roles in place (they
        // would let the UI/route guards grant access the BFF rejects with a 403).
        if (user.value) {
          user.value.roles = []
        }
        return false
      }

      // Call BFF API to get user roles (direct URL to BFF, not through proxy)
      const bffUrl = import.meta.env.VITE_BFF_URL || 'http://localhost:3001/api'
      const response = await fetch(`${bffUrl}/user-roles/me`, {
        headers: {
          Authorization: `Bearer ${tokenResult.value}`,
        },
      })

      if (!response.ok) {
        if (import.meta.env.DEV) {
          console.warn('[AuthStore] Failed to load database roles:', response.status)
        }
        // A refusal from the access gate is not a permissions failure: the member's roles are
        // fine, their ORGANISATION is blocked. Flag it so the router can say so, instead of
        // falling through to the generic Access Denied page (#1809 E2E). Roles still clear below.
        const refusal = await gateRefusalCode(response)
        organisationUnavailable.value = refusal === ORGANISATION_UNAVAILABLE_CODE
        // Fail closed (#561): the DB is the single source of truth for authorization, so on
        // ANY failure clear roles to [] — never leave stale Auth0-claim roles in place (they
        // would let the UI/route guards grant access the BFF rejects with a 403).
        if (user.value) {
          user.value.roles = []
        }
        return false
      }

      organisationUnavailable.value = false
      const data = await response.json()
      const dbRoles: string[] = data.roles?.map((r: { role_name: string }) => r.role_name) || []

      // #561: the DATABASE (user_roles) is the single source of truth for authorization —
      // the BFF enforces ONLY DB roles. We must NOT merge Auth0 token role claims, or the
      // UI would show admin features (and pass route guards) for roles the backend rejects
      // (causing 403s), and a stale/independent Auth0 claim could imply access a user does
      // not actually have. Set the role set to exactly the DB roles so frontend gating
      // matches backend enforcement.
      if (user.value) {
        user.value.roles = dbRoles
      }

      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log('[AuthStore] Roles loaded (DB authoritative):', dbRoles)
      }
      return true
    } catch (err) {
      reportError(err, { where: 'auth.store.loadDatabaseRoles' })
      // Ensure roles array exists even on failure
      if (user.value && !user.value.roles) {
        user.value.roles = []
      }
      // Don't throw - role loading failure shouldn't break authentication
      return false
    }
  }

  /**
   * Stop a signed-in user who belongs to no organisation (#2688). Decided ONCE per auth check,
   * after the roles have loaded, and only when ALL of these hold: the real auth service (not
   * the mock), the user is not a `platform_admin` (global, zero memberships is valid, #2201),
   * the access token names no organisation, and the user's own organisation list (the one
   * OrgSwitcher reads) loaded successfully and is empty. Empty because every membership is
   * disabled (`only_disabled_memberships: true`, Task #3008) records `ALL_ORGANISATIONS_DISABLED`;
   * empty otherwise records `NO_ORGANISATION`. A failed load of anything fails OPEN to today's
   * behaviour. Once access checks are switched on (ACCOUNT_ACCESS_CHECK=enforce, after the
   * post-migrate adoption), the server refuses a person disabled in every organisation (#2791)
   * and the fetch watch records the same block from that 403.
   *
   * NEVER on an invitation (#2791 security finding): an invitee signs in with no organisation
   * and belongs to none until they accept, so the stop is not recorded while the user is on
   * `/invite/*` or the sign-in callback is returning them there. `InviteAcceptPage` clears it
   * after a successful accept in case it was recorded earlier.
   */
  async function blockIfNoOrganisation(): Promise<void> {
    try {
      if (import.meta.env.VITE_ENABLE_MOCK_SERVICES === 'true') return
      if (isInvitePath(window.location.pathname) || isInvitePath(callbackReturnTo)) return
      if (hasRole('platform_admin')) return
      const tokenResult = await authService?.getAccessToken()
      if (!tokenResult?.ok || !tokenResult.value) return
      if (extractOrgId(tokenResult.value) !== '') return

      const bffUrl = import.meta.env.VITE_BFF_URL || 'http://localhost:3001/api'
      const response = await fetch(`${bffUrl}/user/organisations`, {
        headers: { Authorization: `Bearer ${tokenResult.value}` },
      })
      if (!response.ok) return
      const body: unknown = await response.json()
      const list = body as { organisations?: unknown; only_disabled_memberships?: unknown } | null
      if (!Array.isArray(list?.organisations) || list.organisations.length !== 0) return
      if (list.only_disabled_memberships === true) recordAllOrganisationsDisabled()
      else recordNoOrganisation()
    } catch {
      // Fail open: a stop we are not sure of is worse than today's behaviour.
    }
  }

  async function checkAuth(): Promise<AuthResult<void>> {
    if (!authService) {
      return {
        ok: false,
        error: new Error('Auth service not initialized'),
      }
    }

    try {
      const authResult = await authService.isAuthenticated()
      if (!authResult.ok) {
        error.value = authResult.error
        return authResult
      }

      isAuthenticated.value = authResult.value

      if (authResult.value) {
        // Get user profile
        const userResult = await authService.getUser()
        if (!userResult.ok) {
          error.value = userResult.error
          return userResult
        }
        user.value = userResult.value

        // Load database roles and merge with Auth0 roles
        if (user.value?.sub) {
          const rolesLoaded = await loadDatabaseRoles(user.value.sub)
          if (rolesLoaded) await blockIfNoOrganisation()
        }
      } else {
        user.value = null
      }

      return { ok: true, value: undefined }
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Auth check failed')
      error.value = errorObj
      return { ok: false, error: errorObj }
    }
  }

  async function refreshToken(): Promise<AuthResult<string>> {
    if (!authService) {
      return {
        ok: false,
        error: new Error('Auth service not initialized'),
      }
    }

    try {
      error.value = null

      const result = await authService.getAccessToken()
      if (result.ok) {
        return result
      }

      // Token fetch failed. Before propagating the error (which causes services to call
      // emitSessionExpired()), attempt an SSO session renewal via checkSession().
      // auth0Client.checkSession() calls the /authorize endpoint with prompt=none in a
      // hidden iframe, using the Auth0 first-party SSO cookie. If the user's Auth0 session
      // is still alive, this issues a new access token silently. This path is the only
      // renewal mechanism available when refresh tokens are not issued (e.g. when the
      // Auth0 API audience does not have "Allow Offline Access" enabled).
      //
      // IMPORTANT: At this point the Auth0 localStorage cache is already empty (Auth0
      // leeway removed it ~60 s before natural expiry), so checkSession() cannot cause
      // premature cache clearing — there is nothing left to clear.
      //
      // Note: This will FAIL silently in browsers that block third-party cookies (Chrome
      // 80+ with enhanced protection, Safari ITP) because the hidden iframe cannot access
      // the first-party auth0 cookie. In those environments the root fix is either to:
      // - Enable refresh tokens (Auth0 API → Allow Offline Access), or
      // - Increase the access token lifetime in the Auth0 dashboard (APIs → Token Expiration).
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log(
          '[AuthStore] Access token expired — attempting SSO session renewal via checkSession()'
        )
      }

      const sessionResult = await authService.checkSession()
      if (sessionResult.ok) {
        // SSO renewal succeeded — retry token acquisition
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.log(
            '[AuthStore] SSO renewal succeeded — session extended without user interaction'
          )
        }
        const retryResult = await authService.getAccessToken()
        if (retryResult.ok) {
          return retryResult
        }
        // Retry failed even after successful checkSession — fall through to return original error
      } else if (import.meta.env.DEV) {
        console.warn(
          '[AuthStore] SSO renewal failed (third-party cookies blocked or session expired) — session cannot be extended'
        )
      }

      error.value = result.error
      return result
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Token refresh failed')
      error.value = errorObj
      return { ok: false, error: errorObj }
    }
  }

  /**
   * Get auth service instance (for storage initialization)
   */
  function getAuthService(): IAuthService | null {
    return authService
  }

  /**
   * Get access token for API calls
   */
  async function getAccessToken(): Promise<AuthResult<string>> {
    if (!authService) {
      return {
        ok: false,
        error: new Error('Auth service not initialized'),
      }
    }

    try {
      return await authService.getAccessToken()
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Failed to get access token')
      return { ok: false, error: errorObj }
    }
  }

  return {
    // State
    isAuthenticated,
    isLoading,
    user,
    error,
    organisationUnavailable,
    seatRequired,
    // Getters
    currentUser,
    hasPermission,
    hasRole,
    // Actions
    setAuthService,
    getAuthService,
    initialize,
    login,
    logout,
    handleCallback,
    checkAuth,
    refreshToken,
    getAccessToken,
  }
})
